import { CheckCircle2, Clock, MessageSquare, ShieldCheck } from "lucide-react";
import Hero from "../components/Hero";
import ConsultationForm from "../components/ConsultationForm";
import CTABanner from "../components/CTABanner";
import { IMAGES, COPY } from "../lib/_data";

const DEFAULT_EXPECT = [
  {
    icon: MessageSquare,
    title: "A real conversation",
    body:
      "We start with the problem in your words — no jargon, no upsell pressure.",
  },
  {
    icon: CheckCircle2,
    title: "An honest diagnosis",
    body:
      "We inspect the work area, explain what we see, and share the actual root cause.",
  },
  {
    icon: ShieldCheck,
    title: "A written quote",
    body:
      "Itemized labor + materials. No surprises. The price we quote is the price you pay.",
  },
  {
    icon: Clock,
    title: "A clear next step",
    body:
      "If you're in, we schedule it. If not, no hard feelings — we're neighbors either way.",
  },
];
const ICON_POOL = { MessageSquare, CheckCircle2, ShieldCheck, Clock };

export default function Consultations() {
  const hero = COPY.consultations.hero;
  const expect = COPY.consultations.expect;
  const finalCta = COPY.consultations.finalCta;
  const expectItems = (expect.items && expect.items.length ? expect.items.map(x => ({
    icon: ICON_POOL[x.icon] || MessageSquare,
    title: x.title,
    body: x.body,
  })) : DEFAULT_EXPECT).map((e, i) => ({
    ...e,
    title: expect[`item${i + 1}Title`] || e.title,
    body: expect[`item${i + 1}Body`] || e.body,
  }));
  return (
    <div data-testid="page-consultations">
      <Hero
        image={IMAGES.consultationHero}
        eyebrow={hero.eyebrow || "Free · No obligation · No pressure"}
        title={hero.title || "Book your consultation."}
        subtitle={hero.subtitle || "Fill out the form below and a real person on our team will reach out within one business hour to confirm your appointment."}
        trustLine={hero.trustLine || "Same-week availability for most services"}
      />

      <section
        data-testid="consult-body"
        className="bg-[#f9f6f0] py-20 md:py-28"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-14 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
          <div className="lg:col-span-5 lg:sticky lg:top-32">
            <span className="text-xs tracking-[0.24em] uppercase font-bold text-[#c85a3c]" data-testid="consult-expect-eyebrow">
              {expect.eyebrow || "What to expect"}
            </span>
            <h2 className="font-display text-3xl md:text-5xl font-black text-[#2c2a29] leading-[1.05] mt-4" data-testid="consult-expect-title">
              {expect.title || "What happens during a consultation?"}
            </h2>
            <p className="mt-6 text-[#5c5855] leading-relaxed" data-testid="consult-expect-subtitle">
              {expect.subtitle || "Consultations take about 30 minutes and are always free. We'll come to your home (or hop on a quick call if you prefer) and walk you through your options with zero pressure."}
            </p>

            <ul className="mt-10 space-y-6">
              {expectItems.map((e) => (
                <li
                  key={e.title}
                  data-testid={`expect-${e.title.toLowerCase().replace(/\s+/g, "-")}`}
                  className="flex gap-4"
                >
                  <span className="w-11 h-11 rounded-xl bg-[#c85a3c]/10 text-[#c85a3c] flex items-center justify-center flex-shrink-0">
                    <e.icon className="w-5 h-5" strokeWidth={2.4} />
                  </span>
                  <div>
                    <div className="font-display text-lg font-bold text-[#2c2a29]">
                      {e.title}
                    </div>
                    <div className="text-sm text-[#5c5855] mt-1 leading-relaxed">
                      {e.body}
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          </div>

          <div className="lg:col-span-7">
            <ConsultationForm />
          </div>
        </div>
      </section>

      <CTABanner
        image={IMAGES.finalCta}
        eyebrow={finalCta.eyebrow || "Prefer to talk first?"}
        title={finalCta.title || "Give us a call — a human always answers."}
        subtitle={finalCta.subtitle || "If forms aren't your thing, ring us up. We're friendly, we don't oversell, and we'll be honest if we're not the right fit."}
        primary={{ label: finalCta.primaryLabel || "Call Now", to: finalCta.primaryTo || "/contact" }}
        secondary={{ label: finalCta.secondaryLabel || "Contact Us", to: finalCta.secondaryTo || "/contact" }}
        showPhone={false}
      />
    </div>
  );
}
